import { collectCaseFilesCategoryFilterUuids, serializeCaseFilesHistoryRowFilters } from '@/utils';
import { CASES_LIST_TAG } from '@/api/constants';
import { api } from '@/api/core';
export interface CaseFileData {
  documentId: string | null;
  documentName: string;
  uploadedBy: string;
  uploadedAt: string;
  sizeMb: number;
  numberOfPages: number;
  status: string;
  /**
   * Origin of the file (`FileSource`: 'PROCLAIM' | 'UPLOAD'). Kept as a string so an
   * unknown code still reaches `getCaseFileSourceDisplay`. Null for history rows.
   */
  source: string | null;
  historyRowColumns: HistoryRowColumns;
  categories: Categories;
  details: string;
  documentFormat: string;
  hasDocument: boolean;
  isDisplayable: boolean;
  warningDetails: string;
  warnings: string[];
  historyRowId: string;
  attachments?: CaseFileData[];
}

interface CaseFileDataApi extends Omit<CaseFileData, 'documentId' | 'attachments'> {
  documentId: string | null;
  historyRowId: string;
}

export type GroupedCaseFileApi = CaseFileDataApi & {
  attachments?: CaseFileDataApi[];
};

function normalizeCaseFileRow(doc: CaseFileDataApi): CaseFileData {
  const row = { ...(doc as GroupedCaseFileApi) };
  delete row.attachments;
  return row as CaseFileData;
}

export function normalizeGroupedCaseFilesList(grouped: GroupedCaseFileApi[]): CaseFileData[] {
  return grouped.map((parent) => {
    const base = normalizeCaseFileRow(parent);
    const attachments = (parent.attachments ?? []).map((a) => normalizeCaseFileRow(a));
    return {
      ...base,
      attachments: attachments.length > 0 ? attachments : undefined,
    };
  });
}

/**
 * Rows nested under a case file in the table. Read them only through this function, so the BE shape
 * for child rows (e.g. audio -> transcript) is handled in one place.
 */
export function getCaseFileChildRows(file: CaseFileData): CaseFileData[] {
  return file.attachments ?? [];
}

/** Flatten parent rows then each child row (order used by bulk select-all). */
export function flattenCaseFilesWithAttachments(caseFiles: CaseFileData[]): CaseFileData[] {
  const out: CaseFileData[] = [];
  for (const parent of caseFiles) {
    out.push(parent);
    for (const child of getCaseFileChildRows(parent)) {
      out.push(child);
    }
  }
  return out;
}

export interface HistoryRowColumns {
  Handler: string;
  'Corres. to': string;
  'Proclaim Folder': string;
}

export interface Categories {
  Category: string;
  'Sub-Category': string;
}

export interface GetCaseFilesResponse {
  groupedDocuments: GroupedCaseFileApi[];
  numberOfResultsExcludingAttachments: number;
  /** Only returned for the flat (non-grouped) response shape. */
  numberOfResults?: number;
  /** Only returned for the flat (non-grouped) response shape. */
  documents?: CaseFileDataApi[];
}

/** Number of top-level rows for the current filters (`numberOfResultsExcludingAttachments` when grouped). */
export function getCaseFilesTopLevelRowCount(data: GetCaseFilesResponse, fallback: number): number {
  if (data.groupedDocuments.length > 0) {
    return data.numberOfResultsExcludingAttachments ?? fallback;
  }
  return data.numberOfResults ?? data.numberOfResultsExcludingAttachments ?? fallback;
}

export type CaseFilesSelectedFilters = Record<string, string[]>;

export type GetCaseFilesRequest = {
  caseId: string;
  userId: string;
  pageIndex: number;
  pageSize: number;
  searchTerm?: string;
  minDate?: string | null;
  maxDate?: string | null;
  selectedFilters?: CaseFilesSelectedFilters;
  sortDirection?: 'ascending' | 'descending';
};

/** Everything that narrows a matter history query, without the pagination window. */
export type CaseFilesQueryFilters = Omit<GetCaseFilesRequest, 'pageIndex' | 'pageSize'>;

/** Filter params shared by `/matter/history` and `/matter/history-ids`, so both return the same rows. */
export function buildCaseFilesFilterParams({
  caseId,
  userId,
  minDate,
  maxDate,
  sortDirection,
  selectedFilters,
  searchTerm,
}: CaseFilesQueryFilters): Record<string, unknown> {
  const categoryFilters = collectCaseFilesCategoryFilterUuids(selectedFilters);

  const params: Record<string, unknown> = {
    user_id: userId,
    matter_id: caseId,
    sort_direction: sortDirection,
    min_date: minDate ?? undefined,
    max_date: maxDate ?? undefined,
    document_name_or_details: searchTerm ?? undefined,
  };

  if (categoryFilters.length > 0) {
    params.categoryFilters = categoryFilters;
  }

  const historyRowFilters = serializeCaseFilesHistoryRowFilters(selectedFilters);
  if (historyRowFilters !== undefined) {
    params.history_row_filters = historyRowFilters;
  }

  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '') {
      delete params[k];
    }
  }

  return params;
}

function buildGetCaseFilesQueryConfig({ pageIndex, pageSize, ...filters }: GetCaseFilesRequest) {
  return {
    url: '/matter/history',
    method: 'GET' as const,
    params: {
      ...buildCaseFilesFilterParams(filters),
      page_index: pageIndex,
      page_size: pageSize,
    },
  };
}

const getCaseFilesApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getCaseFiles: builder.query<GetCaseFilesResponse, GetCaseFilesRequest>({
      query: (args) => buildGetCaseFilesQueryConfig(args),
      providesTags: (_result, _error, { caseId, pageIndex, pageSize }) => [
        { type: CASES_LIST_TAG, id: `case-files-${caseId}-${pageIndex}-${pageSize}` },
      ],
    }),
    /** Same as getCaseFiles but does not write to the case-files table slice (for bulk select-all). */
    getCaseFilesPageData: builder.query<GetCaseFilesResponse, GetCaseFilesRequest>({
      query: (args) => buildGetCaseFilesQueryConfig(args),
      providesTags: (_result, _error, { caseId, pageIndex, pageSize }) => [
        { type: CASES_LIST_TAG, id: `case-files-page-data-${caseId}-${pageIndex}-${pageSize}` },
      ],
    }),
  }),
});

export const { useLazyGetCaseFilesQuery, useLazyGetCaseFilesPageDataQuery } = getCaseFilesApi;
